import { createReducer, on } from '@ngrx/store';

import BetslipActions from '../../base/store/actions';
import BetbuilderActions from '../bet-builder/actions';
import { QuickBetActions } from './quick-bet-actions';
import {
    QuickBetActionButtonState,
    QuickBetUiState,
    defaultQuickBetActionButtonState,
    defaultQuickBetDisplayHelper,
    defaultQuickBetHelpState,
    defaultQuickBetPlaceResultState,
    defaultQuickBetStakeState,
    defaultQuickBetState,
} from './quick-bet.state';

export const quickBetReducer = createReducer(
    defaultQuickBetState,
    on(QuickBetActions.addPick, (state, action) => {
        const pickId = action.pickId;
        if (state.pickIds.some((p) => p.isEqual(pickId))) {
            return state;
        }

        return {
            ...state,
            pickIds: [...state.pickIds, action.pickId],
        };
    }),
    on(QuickBetActions.setActionButton, (state, action) => {
        const isProcessing =
            action.payload === QuickBetActionButtonState.Placing ||
            action.payload === QuickBetActionButtonState.PlacingFreeBet ||
            action.payload === QuickBetActionButtonState.ProcessingDeposit ||
            action.payload === QuickBetActionButtonState.ProcessingDepositAndBet;

        return {
            ...state,
            actionButtonState: {
                ...state.actionButtonState,
                state: action.payload,
                isDisabled: state.isDisabled || isProcessing,
                isProcessing,
            },
        };
    }),
    on(QuickBetActions.setHasLockedPick, (state, action) => {
        return {
            ...state,
            hasLockedPick: action.payload.hasLockedPick,
        };
    }),
    on(QuickBetActions.setHasClosedPick, (state, action) => {
        return {
            ...state,
            hasClosedPick: action.payload.hasClosedPick,
        };
    }),
    on(QuickBetActions.init, (state, action) => {
        return {
            ...state,
            helpState: defaultQuickBetHelpState,
            uiState: QuickBetUiState.Loading,
        };
    }),
    on(QuickBetActions.openQuickBet, (state, action) => {
        const uiState = QuickBetUiState.Place;

        return {
            ...state,
            uiState,
        };
    }),
    on(QuickBetActions.closeQuickBet, (state, _) => {
        return {
            ...defaultQuickBetState,
            helpState: state.helpState,
            betPlacedResult: state.betPlacedResult,
            inOverlay: state.inOverlay,
        };
    }),
    on(QuickBetActions.cleanQuickBetAfterCloseUI, (state, _) => {
        return {
            ...defaultQuickBetState,
            stakeState: { ...defaultQuickBetStakeState },
            uiState: QuickBetUiState.None,
            helpState: state.helpState,
            betPlacedResult: state.betPlacedResult,
            inOverlay: state.inOverlay,
            inEditBetMode: state.inEditBetMode,
            actionButtonState: { ...defaultQuickBetActionButtonState },
        };
    }),
    on(QuickBetActions.closeQuickBetUiOverlay, (state, _) => {
        return {
            ...defaultQuickBetState,
            stakeState: state.stakeState,
            uiState: QuickBetUiState.None,
            helpState: state.helpState,
            betPlacedResult: state.betPlacedResult,
            inOverlay: state.inOverlay,
            inEditBetMode: state.inEditBetMode,
            actionButtonState: { ...state.actionButtonState },
        };
    }),
    on(QuickBetActions.closeConfirmation, (state, _) => {
        return {
            ...defaultQuickBetState,
            helpState: state.helpState,
            betPlacedResult: state.betPlacedResult,
            inOverlay: state.inOverlay,
            uiState: QuickBetUiState.None,
        };
    }),
    on(QuickBetActions.updatePlaceBetResult, (state, action) => {
        return {
            ...state,
            betPlacedResult: action.payload,
        };
    }),
    on(QuickBetActions.showPlaceBetWarning, (state, action) => {
        return {
            ...state,
            isDisabled: false,
            actionButtonState: {
                ...state.actionButtonState,
                isProcessing: false,
                isDisabled: false,
                state: QuickBetActionButtonState.Place,
            },
        };
    }),
    on(QuickBetActions.setStakeState, (state, action) => {
        return {
            ...state,
            stakeState: {
                ...state.stakeState,
                stake: action.stake,
            },
        };
    }),
    on(QuickBetActions.resetResultState, (state, action) => {
        return {
            ...state,
            betPlacedResult: defaultQuickBetPlaceResultState,
        };
    }),
    on(QuickBetActions.placeBet, (state, action) => {
        return {
            ...state,
            isDisabled: true,
            pickLockedOnBetPlacement: false,
            oddsChangedOnBetPlacement: false,
            actionButtonState: {
                ...state.actionButtonState,
            },
            betPlacedResult: defaultQuickBetPlaceResultState,
        };
    }),
    on(QuickBetActions.openConfirmation, (state, action) => {
        return {
            ...state,
            uiState: QuickBetUiState.Success,
        };
    }),
    on(QuickBetActions.showPickChangedWarning, (state, action) => {
        const isPlacingFreeBet = state.actionButtonState.state === QuickBetActionButtonState.PlacingFreeBet;
        let buttonState;
        if (action.payload.pickLocked || !action.payload.isAcceptAndPlaceBetEnabled) {
            buttonState = isPlacingFreeBet ? QuickBetActionButtonState.PlaceFreeBet : QuickBetActionButtonState.Place;
        } else {
            buttonState = isPlacingFreeBet ? QuickBetActionButtonState.AcceptAndPlaceFreeBet : QuickBetActionButtonState.AcceptAndPlace;
        }

        return {
            ...state,
            isDisabled: false,
            oddsChangedOnBetPlacement: action.payload.oddsChanged,
            pickLockedOnBetPlacement: action.payload.pickLocked,
            uiState: QuickBetUiState.Place,
            actionButtonState: {
                ...state.actionButtonState,
                isDisabled: action.payload.pickLocked,
                state: buttonState,
            },
        };
    }),
    on(QuickBetActions.keepOpen, (state, action) => {
        return {
            ...state,
            keepOpen: action.open,
        };
    }),
    on(QuickBetActions.login, QuickBetActions.makeDeposit, QuickBetActions.viewBonus, (state, action) => {
        return {
            ...state,
            keepOpen: true,
        };
    }),
    on(QuickBetActions.showHelp, (state, action) => {
        return {
            ...state,
            helpState: {
                wasShown: false,
                isVisible: true,
            },
        };
    }),
    on(QuickBetActions.hideHelp, (state, action) => {
        return {
            ...state,
            helpState: {
                wasShown: true,
                isVisible: false,
            },
        };
    }),
    on(QuickBetActions.numpadAction, (state, action) => {
        const numpadAction = action.action;

        return {
            ...state,
            stakeState: {
                ...state.stakeState,
                numpadAction,
            },
        };
    }),
    on(BetslipActions.eventUpdate, BetslipActions.fixtureUpdate, (state, action) => {
        return {
            ...state,
            betPlacedResult: {
                ...state.betPlacedResult,
                warnings: [],
            },
            actionButtonState: {
                ...state.actionButtonState,
                isDisabled: state.isDisabled,
            },
        };
    }),
    on(QuickBetActions.clearWarnings, (state, action) => {
        return {
            ...state,
            betPlacedResult: {
                ...state.betPlacedResult,
                warnings: [],
            },
        };
    }),
    on(BetbuilderActions.updateBetBuilderPick, (state, action) => {
        return {
            ...state,
            eventId: action.payload.eventId,
        };
    }),
    on(QuickBetActions.resetUiState, (state) => {
        return {
            ...state,
            uiState: QuickBetUiState.None,
        };
    }),
    on(QuickBetActions.removePick, (state, action) => {
        const pickId = action.pickId;
        const newPickIds = state.pickIds.filter((p) => !p.isEqual(pickId));

        return {
            ...state,
            pickIds: newPickIds,
        };
    }),
    on(QuickBetActions.removeMultiplePicks, (state, action) => {
        const pickIdsToRemove = new Set(action.pickIds.map((pickId) => pickId.toString()));
        const newPickIds = state.pickIds.filter((p) => !pickIdsToRemove.has(p.toString()));

        return {
            ...state,
            pickIds: newPickIds,
        };
    }),
    on(QuickBetActions.setDisplayHelperToDefault, (state, _) => {
        return {
            ...state,
            displayHelper: {
                ...defaultQuickBetDisplayHelper,
            },
        };
    }),
    on(QuickBetActions.replacePick, (state, action) => {
        // updating the pickIds in main quickBet state
        const pickId = action.oldPickId.toString();
        const pickIds = state.pickIds.map((id) => (id.toString() === pickId ? action.newPickId : id));

        return { ...state, pickIds };
    }),
    on(QuickBetActions.setHiddenByError, (state, action) => {
        return {
            ...state,
            displayHelper: {
                ...state.displayHelper,
                hiddenByError: action.isHidden,
            },
        };
    }),
    on(QuickBetActions.openBetslip, (state, action) => {
        return {
            ...state,
            displayHelper: {
                ...state.displayHelper,
                addedToBetslip: true,
            },
        };
    }),
    on(QuickBetActions.resetAddedToBetslip, (state, action) => {
        return {
            ...state,
            displayHelper: {
                ...state.displayHelper,
                addedToBetslip: false,
            },
        };
    }),
    on(QuickBetActions.resetToPlaceState, (state, action) => {
        return {
            ...state,
            uiState: QuickBetUiState.Place,
            isDisabled: false,
            actionButtonState: { ...defaultQuickBetActionButtonState },
            betPlacedResult: { ...defaultQuickBetPlaceResultState },
        };
    }),
);
